import React, { useState } from "react";
import { useParams } from "react-router-dom";

import { Box, Chip, CircularProgress, Divider, Switch, Typography, ToggleButtonGroup, ToggleButton, Button } from "@mui/material";
import { Add, GridViewOutlined, ViewTimelineOutlined, InfoOutline } from "@mui/icons-material";
import { useFetchTask } from "../hooks/useFetchTask";
import { DrawerControl, SearchField } from "../../../shared/components";

import { TaskDetails, RaiseTask, TableView, AllTasks } from "../components/index";
import { useDisplayField } from "../../../hooks/useDisplayField";

export const Task: React.FC = () => {

    const { projectId } = useParams<{ projectId: string }>();
    const { getFilterOptions } = useDisplayField([]);

    const [alignment, setAlignment] = useState<string | null>("center");
    const [raiseTask, setRaiseTask] = useState<boolean>(false);

    const [priorityFilter, setPriorityFilter] = useState<string | null>(null);
    const [actionTypeFilter, setActionTypeFilter] = useState<string>('all');
    const [statusFilter, setStatusFilter] = useState<string | null>(null);

    const [assignedToMe, setAssignedToMe] = useState(false);

    const [details, setDetails] = useState({
        open: false,
        taskId: null as string | null,
    });

    const [editStatus, setEditStatus] = useState({
        open: false,
        taskId: null as string | null,
    });

    const onHandleEdit = () => {
        setEditStatus({ open: true, taskId: details.taskId });
        setDetails({ open: false, taskId: null });
    };

    const handleAlignment = (_e: React.MouseEvent<HTMLElement>, newAlignment: string | null) => {
        setAlignment(newAlignment);
    };

    const {
        isPending, isRefetching, taskData, pagination, searchDraft,
        handleChangeSearch, handleClear, handleSearch,
        changePage, changeRowsPerPage, refetch, switchStatus
    } = useFetchTask({ projectId: projectId, object_type: actionTypeFilter, object_id: null, my_tasks: assignedToMe, priorityFilter: priorityFilter || undefined, statusFilter: statusFilter || undefined });

    const handleStatusFilterChange = (status: string) => {
        if (status === statusFilter) {
            setStatusFilter(null);
            return;
        }
        setStatusFilter(status);
    };

    const handleActionTypeChange = (type: string) => {
        setActionTypeFilter(type);
    };

    const handlePriorityFilterChange = (priority: string) => {
        if (priority === priorityFilter) {
            setPriorityFilter(null);
            return;
        }
        setPriorityFilter(priority);
    }

    const handleAssignedToMeChange = () => {
        setAssignedToMe(!assignedToMe);
    };

    const handleStatusChange = async (taskId: string, newStatus: string) => {
        await switchStatus(taskId, newStatus);
        refetch();
    };

    if (isPending) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        );
    };

    return (
        <Box display="flex" flexDirection="column" height="100%">

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'flex-start', flexWrap: 'wrap', p: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', ml: 'auto' }}>
                    <Typography variant="subtitle2">Priority: </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {
                            getFilterOptions('TASK_PRIORITY').map((domain: any) => (
                                <Chip
                                    key={domain}
                                    label={domain.display_text}
                                    color={domain.value === priorityFilter ? 'primary' : 'default'}
                                    variant={domain.value === priorityFilter ? 'filled' : 'outlined'}
                                    onClick={() => handlePriorityFilterChange(domain.value)}
                                />
                            ))
                        }
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Typography variant="subtitle2">Status: </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {
                            getFilterOptions('TASK_STATUS').map((domain: any) => (
                                <Chip
                                    key={domain}
                                    label={domain.display_text}
                                    color={domain.value === statusFilter ? 'primary' : 'default'}
                                    variant={domain.value === statusFilter ? 'filled' : 'outlined'}
                                    onClick={() => handleStatusFilterChange(domain.value)}
                                />
                            ))
                        }
                    </Box>
                </Box>
                <Button startIcon={<Add />} onClick={() => setRaiseTask(true)}>raise new task</Button>
            </Box>

            <Divider />

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'start',
                    alignItems: 'center',
                    gap: 2,
                    p: 2
                }}
            >
                <ToggleButtonGroup
                    value={alignment}
                    exclusive
                    onChange={handleAlignment}
                    aria-label="view toggle"
                    size="small"
                >
                    <ToggleButton value="center" aria-label="list view">
                        <ViewTimelineOutlined sx={{ color: "text.secondary" }} />
                    </ToggleButton>
                    <ToggleButton value="left" aria-label="grid view">
                        <GridViewOutlined sx={{ color: "text.secondary" }} />
                    </ToggleButton>
                </ToggleButtonGroup>
                <SearchField
                    searchTerm={searchDraft}
                    handleSearch={handleChangeSearch}
                    handleClearSearch={handleClear}
                    handleApplySearch={handleSearch}
                />
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', ml: 'auto' }}>
                    <Typography variant="subtitle2">Assigned to Me: </Typography>
                    <Switch checked={assignedToMe} onChange={handleAssignedToMeChange} />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Typography variant="subtitle2">Action Type: </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {
                            getFilterOptions('TASK_ACTION').map((domain: any) => (
                                <Chip
                                    key={domain}
                                    label={domain.display_text}
                                    color={domain.value === actionTypeFilter ? 'primary' : 'default'}
                                    variant={domain.value === actionTypeFilter ? 'filled' : 'outlined'}
                                    onClick={() => handleActionTypeChange(domain.value)}
                                />
                            ))
                        }
                    </Box>
                </Box>
            </Box>

            <Divider />

            {
                isRefetching ?
                    <Box
                        height="100%"
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        width="100%"
                        gap={2}
                    >
                        <CircularProgress />
                    </Box> :
                    alignment === 'center' ?
                        <TableView
                            taskData={taskData}
                            setDetails={setDetails}
                            pagination={pagination}
                            changeRowsPerPage={changeRowsPerPage}
                            changePage={changePage}
                        />
                        :
                        <AllTasks
                            taskData={taskData}
                            handleStatusChange={handleStatusChange}
                            onEdit={(taskId) => {
                                setEditStatus({ open: true, taskId: taskId });
                            }}
                            pagination={pagination}
                            changeRowsPerPage={changeRowsPerPage}
                            changePage={changePage}
                        />
            }

            <DrawerControl
                title={"Foundation Design Review"}
                subTitle={""}
                isOpen={details.open}
                handleClose={() => setDetails({ open: false, taskId: null })}
                children={details.taskId && <TaskDetails taskId={details.taskId} onEdit={onHandleEdit} onClose={() => setDetails({ open: false, taskId: null })} />}
            />

            <DrawerControl
                title={"Create/Edit Task"}
                subTitle={""}
                isOpen={editStatus.open}
                handleClose={() => { setEditStatus({ open: false, taskId: null }) }}
                children={
                    <RaiseTask
                        isEdit={true}
                        taskId={editStatus.taskId}
                        onClose={() => {
                            setDetails({ open: true, taskId: editStatus.taskId })
                            setEditStatus({ open: false, taskId: null })
                        }}
                        refetch={refetch}
                        enabledFields={['task_category']}
                    />
                }
            />

            <DrawerControl
                title={"Create/Edit Task"}
                subTitle={""}
                isOpen={raiseTask}
                handleClose={() => { setRaiseTask(false) }}
                children={
                    <RaiseTask
                        isEdit={false}
                        taskId={null}
                        onClose={() => { setRaiseTask(false) }}
                        refetch={() => refetch()}
                        enabledFields={['task_category']}
                    />
                }
            />

        </Box>
    );
};